<template>
  <div id="app" class="ali-container">
    <!-- 顶部搜索区 -->
    <el-card shadow="hover" class="search-card">
      <el-row :gutter="12">
        <el-col :span="18">
          <el-input
            v-model="searchKeyword"
            placeholder="请输入展位编号搜索"
            size="mini"
            @input="onSearchInput"
            @keyup.enter="searchBooths"
            prefix-icon="el-icon-search"
          ></el-input>
        </el-col>
        <el-col :span="3">
          <el-button type="primary" size="mini" @click="searchBooths">搜索</el-button>
        </el-col>
        <el-col :span="3">
          <el-button size="mini" @click="clearSearch">清空</el-button>
        </el-col>
      </el-row>
    </el-card>

    <!-- 主画布区域 -->
    <el-card shadow="hover" class="map-card">
     <div class="map-area" ref="mapArea">
  <!-- Canvas图层：删掉全部:style宽高，改用CSS控制 -->
  <canvas
    class="map-canvas"
    :class="{ 'canvas-behind': showBoothDetail }"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchEnd"
    @gesturestart="onGestureStart"
    @gesturechange="onGestureChange"
    @gestureend="onGestureEnd"
  @mousedown="onMouseDown"
  @mousemove="onMouseMove"
  @mouseup="onMouseUp"
  @mouseleave="onMouseUp"
  ></canvas>
  <!-- 底图容器 -->
  <div
    class="map-content"
    :class="{ scaling: isScaling || isPinching }"
    :style="{
      transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
      'transform-origin': '0 0'
    }"
  >
    <img
      class="map-image"
      :src="mapImage||'https://api.shuangma88.com/expo/qingdao_1.webp'"
      @load="onImageLoad"
      @error="onImageError"
      alt="展会平面图"
    />
  </div>
  <div class="zoom-hint" v-if="showZoomHint">双指缩放已启用</div>
</div>
    </el-card>

    <!-- 缩放控制栏 -->
    <el-card shadow="hover" class="control-card">
      <el-row align="middle" justify="space-between">
        <el-col>
          <el-button size="mini" @click="zoomOut">缩小</el-button>
          <el-button size="mini" @click="reset">重置</el-button>
          <el-button size="mini" @click="zoomIn">放大</el-button>
          <span class="scale-text">缩放: {{ (scale * 100).toFixed(0) }}%</span>
          <el-button
            size="mini"
            :type="batchSelectionMode ? 'primary' : ''"
            @click="toggleSelectionMode"
          >
            {{ batchSelectionMode ? '批量选择中' : '批量选择' }}
          </el-button>
        </el-col>
      </el-row>
    </el-card>

    <!-- 展位统计 & 图例 -->
    <el-card shadow="hover" class="stat-card">
      <el-row :gutter="20">
        <el-col :span="14">
          <div class="stat-row">
            <span class="stat-label">展位总数：</span>
            <span class="stat-value">{{ boothStatistics.total }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">已占用：</span>
            <span class="stat-value occupied">
              {{ boothStatistics.occupied }}
              <span class="stat-percent">({{ boothStatistics.occupiedPercent }}%)</span>
            </span>
            <span class="color-dot red"></span>
          </div>
          <div class="stat-row">
            <span class="stat-label">未占用：</span>
            <span class="stat-value available">
              {{ boothStatistics.available }}
              <span class="stat-percent">({{ boothStatistics.availablePercent }}%)</span>
            </span>
            <span class="color-dot green"></span>
          </div>
        </el-col>
        <el-col :span="10">
          <div class="legend-title">图例说明</div>
          <div class="legend-list">
            <div class="legend-item">
              <span class="color-dot red"></span>
              <span>已占用</span>
            </div>
            <div class="legend-item">
              <span class="color-dot green"></span>
              <span>未占用</span>
            </div>
            <div class="legend-item">
              <span class="color-dot blue"></span>
              <span>已选择</span>
            </div>
            <div class="legend-item">
              <span class="color-dot gold"></span>
              <span>搜索结果</span>
            </div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- 搜索结果面板 -->
    <el-card shadow="hover" class="search-result-card" v-if="searchResult && searchResult.length > 0">
      <div class="result-head">
        <span>找到 {{ searchResult.length }} 个展位</span>
        <div class="result-btns">
          <el-button size="mini" @click="focusOnSearchResult">定位结果</el-button>
          <el-button size="mini" type="primary" @click="selectAllSearchResult">全选</el-button>
        </div>
      </div>
      <div class="result-list">
        <div
          v-for="booth in searchResult"
          :key="booth.id"
          class="result-item"
          @click="focusOnBooth(booth)"
          :class="{ selected: booth.selected }"
        >
          {{ booth.name }}
          <span class="dot" :class="{ active: booth.selected }"></span>
        </div>
      </div>
    </el-card>

    <!-- 已选展位 -->
    <el-card shadow="hover" class="selected-card">
      <div class="selected-head">
        <span>已选展位：{{ selectedBooths.length }} 个</span>
        <el-button size="mini" type="danger" @click="clearSelection">清空选择</el-button>
      </div>
      <div class="selected-list">
        <div v-for="booth in selectedBooths" :key="booth.id" class="selected-item">
          {{ booth.name }}
          <i class="el-icon-close close-tag" @click="removeBooth(booth.id)"></i>
        </div>
      </div>
    </el-card>

    <!-- 展位详情弹窗 -->
    <el-dialog
      title="展位详情"
      :visible.sync="showBoothDetail"
      width="520px"
      @close="closeBoothDialog"
    >
      <div v-if="currentBooth" class="booth-detail">
        <div class="detail-row">
          <span class="label">展位编号</span>
          <span class="value">{{ currentBooth.name }}</span>
        </div>
        <div class="detail-row">
          <span class="label">面积</span>
          <span class="value">{{ currentBooth.area }} ㎡</span>
        </div>
        <div class="detail-row">
          <span class="label">金额</span>
          <span class="value price">{{ currentBooth.amount }} 元</span>
        </div>
        <div class="detail-row">
          <span class="label">占用状态</span>
          <span class="value" :class="currentBooth.occupied ? 'text-red' : 'text-green'">
            {{ currentBooth.occupied ? '已占用' : '未占用' }}
          </span>
        </div>
        <div v-if="currentBooth.occupied" class="detail-row">
          <span class="label">入驻商家</span>
          <div class="value">
            <div v-for="(it, idx) in currentBooth.orderinfo" :key="idx">
              {{ it?.ordershop?.name || '无' }}
            </div>
          </div>
        </div>
        <div v-if="!currentBooth.occupied" class="detail-row">
          <span class="label">选择状态</span>
          <span class="value text-blue">{{ currentBooth.selected ? '已选择' : '未选择' }}</span>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="closeBoothDialog">取消</el-button>
        <el-button
          v-if="currentBooth && !currentBooth.occupied"
          type="primary"
          @click="selectCurrentBooth"
        >
          {{ currentBooth.selected ? '取消选择' : '选择展位' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 支付宝风格支付弹窗 -->
    <el-dialog
      title="订单确认"
      :visible.sync="isPay"
      width="540px"
      :before-close="closePayDialog"
    >
      <div class="pay-dialog-wrap">
        <!-- 订单信息 -->
        <div class="pay-block">
          <div class="block-title">订单信息</div>
          <div class="order-info-item">
            <span class="info-label">订单号</span>
            <span class="info-value">{{ orderInfo.orderNumber }}</span>
          </div>
          <div class="order-info-item">
            <span class="info-label">展位号</span>
            <span class="info-value">{{ orderInfo.sn }}</span>
          </div>
          <div class="order-info-item">
            <span class="info-label">面积</span>
            <span class="info-value">{{ orderInfo.area }}㎡</span>
          </div>
          <div class="order-info-item">
            <span class="info-label">展位数量</span>
            <span class="info-value">{{ selectedBooths.length }}个</span>
          </div>
          <div class="order-info-item total">
            <span class="info-label">应付金额</span>
            <span class="info-value total-price">¥{{ orderInfo.totalAmount }}</span>
          </div>
        </div>

        <!-- 支付方式 -->
        <div class="pay-block">
          <div class="block-title">选择支付方式</div>
          <div class="pay-method-list">
            <div
              class="pay-method-item"
              :class="{ active: paymentMethod === 'wechat' }"
              @click="selectPaymentMethod('wechat')"
            >
              <div class="method-left">
                <div class="icon wechat">微</div>
                <div class="method-text">
                  <div class="name">微信支付</div>
                  <div class="desc">推荐使用微信支付</div>
                </div>
              </div>
              <div class="radio" :class="{ checked: paymentMethod === 'wechat' }"></div>
            </div>
            <div
              class="pay-method-item"
              :class="{ active: paymentMethod === 'offline' }"
              @click="selectPaymentMethod('offline')"
            >
              <div class="method-left">
                <div class="icon offline">线</div>
                <div class="method-text">
                  <div class="name">线下支付</div>
                  <div class="desc">联系工作人员现场缴费</div>
                </div>
              </div>
              <div class="radio" :class="{ checked: paymentMethod === 'offline' }"></div>
            </div>
          </div>
        </div>

        <!-- 支付提示 -->
        <div class="pay-tip" v-if="paymentMethod">
          <i class="el-icon-warning-outline tip-icon"></i>
          <span class="tip-text">
            {{ paymentMethod === 'wechat'
              ? '支付成功后展位自动锁定保留'
              : '线下支付请24小时内联系工作人员完成缴费' }}
          </span>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button type="danger" @click="goPay" :loading="isPaying">立即支付</el-button>
      </div>
    </el-dialog>

    <!-- 底部Tab导航 -->
   
  </div>
</template>

<script>
 //  import { mapGetters } from 'vuex'
//import store from '@/store'
import { debounce } from '@/utils/optimize'
// import { findBoothList, findBoothOne, findExpoOne, boothCreateOrder } from '@/api/api'
//import { ApiUrl, Csn, AppId, CompanyId } from '@/config/base'
//import { Sign } from '@/common/login'

let timerList = []

function clearAllTimer() {
  timerList.forEach(t => clearTimeout(t))
  timerList = []
}

export default {
  name: 'BoothMap',
  data() {
    return {
      isPaying: false,
      payResult: null,
      parent_id: '',
      expo_id: 4,
      proxy_id: '',
      expoInfo: null,
      isPay: false,
      paymentMethod: 'wechat',
      orderInfo: {
        orderNumber: 'A20333300010222',
        totalAmount: 0,
        sn: '',
        area: ''
      },
      hasWechatIcon: false,
      hasOfflineIcon: false,
      totalBooths: 0,
      occupiedBooths: 0,
      availableBooths: 0,
      showBoothDetail: false,
      currentBooth: null,
      mapImage: '',
      imageWidth: 0,
      imageHeight: 0,
      containerWidth: 0,
      containerHeight: 0,
      position: { x: 0, y: 0 },
      scale: 1.0,
      lastScale: 1.0,
      initialScale: 1.0,
      isMoving: false,
      isScaling: false,
      isPinching: false,
      startX: 0,
      startY: 0,
      lastTouch: null,
      lastDistance: 0,
      initialCenter: null,
      minScale: 0.1,
      maxScale: 5,
      showZoomHint: false,
      mapAreaRect: null,
      lastTouchTime: 0,
      touchTimer: null,
      isContainerReady: false,
      batchSelectionMode: false,
      selectionQueue: [],
      isProcessingQueue: false,
      pinchStartScale: 1.0,
      pinchStartDistance: 0,
      pinchStartCenter: { x: 0, y: 0 },
      pinchStartPosition: { x: 0, y: 0 },
      smoothScaleTarget: 1.0,
      isSmoothScaling: false,
      smoothScaleTimer: null,
      searchKeyword: '',
      searchResult: [],
      lastSearchKeyword: '',
      bounds: { minX: 0, maxX: 0, minY: 0, maxY: 0 },
      booths: [],
      boothMap: new Map(),
      lastDrawTime: 0,
      drawThrottle: 16,
      visibleBooths: new Set(),
      viewportCache: null,
     
      searchTimer: null
    }
  },
  computed: {
  
   
    selectedBooths() {
      return this.booths.filter(booth => booth.selected)
    },
    boothStatistics() {
      return {
        total: this.totalBooths,
        occupied: this.occupiedBooths,
        available: this.availableBooths,
        occupiedPercent: this.totalBooths > 0 ? ((this.occupiedBooths / this.totalBooths) * 100).toFixed(1) : '0.0',
        availablePercent: this.totalBooths > 0 ? ((this.availableBooths / this.totalBooths) * 100).toFixed(1) : '0.0'
      }
    }
  },
 async mounted() {
  this.debouncedDraw = debounce(() => this.drawBooths(), 16)
  this.checkIconResources()
  this.$nextTick(async () => {
    // 1. 先获取容器宽高，保证containerWidth有值
    await this.updateContainerInfo()
    // 2. 加载图片
    await this.getExpoOne();
    // 3. 加载展位
    await this.initBoothsData()

    setTimeout(() => this.updateContainerInfo(), 500)
    window.addEventListener('resize', () => {
      setTimeout(() => this.updateContainerInfo(), 300)
    })
  })
},
  beforeDestroy() {
    clearAllTimer()
    if (this.debouncedDraw?.cancel) this.debouncedDraw.cancel()
    if (this.searchTimer) clearTimeout(this.searchTimer)
    if (this.smoothScaleTimer) cancelAnimationFrame(this.smoothScaleTimer)
  },
  methods: {
  
   // ====================== 鼠标拖拽PC端逻辑 ======================
onMouseDown(e) {
  e.preventDefault()
  this.isMoving = true
  // 记录鼠标按下初始偏移
  this.startX = e.clientX - this.position.x
  this.startY = e.clientY - this.position.y
  // 同时触发展位点击检测
  this.onCanvasTouch({ touches: [e] })
},
onMouseMove(e) {
  if (!this.isMoving) return
  e.preventDefault()
  // 计算新坐标
  const nx = e.clientX - this.startX
  const ny = e.clientY - this.startY
  // 边界限制
  this.position.x = Math.max(this.bounds.minX, Math.min(this.bounds.maxX, nx))
  this.position.y = Math.max(this.bounds.minY, Math.min(this.bounds.maxY, ny))
  this.updateVisibleBooths()
  this.scheduleDraw()
},
onMouseUp() {
  this.isMoving = false
  this.enforceBounds()
},
    // 获取展会基础信息
    async getExpoOne() {
      const param = { access_token: sessionStorage.getItem('token'), 
      company_id: sessionStorage.getItem('company_id'), 
       id: this.expo_id }
    
      const res = await this.$axios.findExpoOne(param)
      if (res.code === 200) {
        this.expoInfo = res.data
        if (this.expoInfo.proxy_id) {
          this.proxy_id = this.expoInfo.proxy_id
        }
        if (this.isLogin && this.expoInfo.status === 2) {
          this.$router.push('/subpages/user/pages/booth/expoProxy')
        }
        const imgItem = res.data.expo_file.find(item => item.type === 1)
        if (imgItem?.file_url) this.mapImage = imgItem.file_url
      }
    },
    // 搜索防抖
    onSearchInput() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        const key = this.searchKeyword.trim()
        if (key !== this.lastSearchKeyword) this.searchBooths()
      }, 300)
    },
    searchBooths() {
      const keyword = this.searchKeyword.trim().toLowerCase()
      this.lastSearchKeyword = keyword
      if (!keyword) {
        this.searchResult = []
        this.booths.forEach(b => b.searched = false)
        this.scheduleDraw()
        return
      }
      this.searchResult = this.booths.filter(b => b.name.toLowerCase().includes(keyword))
      this.booths.forEach(b => b.searched = this.searchResult.some(r => r.id === b.id))
      this.scheduleDraw()
      if (this.searchResult.length === 1) this.focusOnBooth(this.searchResult[0])
    },
    clearSearch() {
      this.searchKeyword = ''
      this.searchResult = []
      this.lastSearchKeyword = ''
      this.booths.forEach(b => b.searched = false)
      this.scheduleDraw()
    },
    focusOnSearchResult() {
      if (!this.searchResult.length) return
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
      this.searchResult.forEach(b => {
        const box = this.getBoothBounds(b)
        minX = Math.min(minX, box.left)
        maxX = Math.max(maxX, box.right)
        minY = Math.min(minY, box.top)
        maxY = Math.max(maxY, box.bottom)
      })
      const cx = (minX + maxX) / 2
      const cy = (minY + maxY) / 2
      const sx = this.containerWidth / ((maxX - minX) * 1.2)
      const sy = this.containerHeight / ((maxY - minY) * 1.2)
      const targetScale = Math.min(sx, sy, this.maxScale)
      this.smoothScaleTo(targetScale, cx, cy)
    },
    smoothScaleTo(targetScale, targetCX, targetCY) {
      if (this.isSmoothScaling) return
      this.isSmoothScaling = true
      const sStart = this.scale
      const xStart = this.position.x
      const yStart = this.position.y
      const duration = 300
      const start = Date.now()
      const targetX = this.containerWidth / 2 - targetCX * targetScale
      const targetY = this.containerHeight / 2 - targetCY * targetScale
      const animate = () => {
        const t = Date.now() - start
        const p = Math.min(t / duration, 1)
        const ease = 1 - Math.pow(1 - p, 3)
        this.scale = sStart + (targetScale - sStart) * ease
        this.position.x = xStart + (targetX - xStart) * ease
        this.position.y = yStart + (targetY - yStart) * ease
        this.updateBounds()
        this.scheduleDraw()
        if (p < 1) {
          requestAnimationFrame(animate)
        } else {
          this.isSmoothScaling = false
        }
      }
      animate()
    },
    focusOnBooth(booth) {
      if (!booth) return
      const box = this.getBoothBounds(booth)
      const cx = (box.left + box.right) / 2
      const cy = (box.top + box.bottom) / 2
      this.smoothScaleTo(Math.min(2, this.maxScale), cx, cy)
    },
    selectAllSearchResult() {
      if (!this.searchResult.length) return
      this.searchResult.forEach(b => b.selected = true)
      this.scheduleDraw()
      this.$message.success(`已选中${this.searchResult.length}个展位`)
    },
    // 加载展位列表
    async initBoothsData() {
      try {
        const param = {  access_token: sessionStorage.getItem('token'), company_id: sessionStorage.getItem('company_id'), expo_id: this.expo_id }

        const res = await this.$axios.findBoothList(param)
        let occupied = 0, available = 0
        this.booths = res.data.map(item => {
          const isOcc = item.status === 1 || item.occupied === 1
          if (isOcc) occupied++
          else available++
          return {
            id: item.id,
            name: item.sn,
            points: [{ x: item.x1, y: item.y1 }, { x: item.x2, y: item.y2 }, { x: item.x3, y: item.y3 }, { x: item.x4, y: item.y4 }],
            selected: false,
            searched: false,
            occupied: isOcc,
            color: isOcc ? '#ff0000' : '#00ff00',
            area: item.area,
            amount: item.amount
          }
        })
        this.totalBooths = res.data.length
        this.occupiedBooths = occupied
        this.availableBooths = available
        this.boothMap.clear()
        this.booths.forEach(b => this.boothMap.set(b.id, b))
      } catch (err) {
        console.error('展位加载失败', err)
        this.$message.error('展位数据加载失败')
      }
    },
    // 获取容器尺寸
    updateContainerInfo() {
      return new Promise(resolve => {
        const dom = this.$refs.mapArea
        if (!dom) {
          setTimeout(() => this.updateContainerInfo().then(resolve), 100)
          return
        }
        const rect = dom.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          this.containerWidth = rect.width
          this.containerHeight = rect.height
          this.mapAreaRect = rect
          this.isContainerReady = true
          this.updateBounds()
          resolve(rect)
        } else {
          setTimeout(() => this.updateContainerInfo().then(resolve), 100)
        }
      })
    },
    ensureContainerReady() {
      return this.isContainerReady ? Promise.resolve(this.mapAreaRect) : this.updateContainerInfo()
    },
    updateBounds() {
      if (!this.imageWidth || !this.imageHeight) return
      const sw = this.imageWidth * this.scale
      const sh = this.imageHeight * this.scale
      this.bounds.minX = Math.min(0, this.containerWidth - sw)
      this.bounds.maxX = 0
      this.bounds.minY = Math.min(0, this.containerHeight - sh)
      this.bounds.maxY = 0
      if (sw < this.containerWidth) this.bounds.minX = this.bounds.maxX = (this.containerWidth - sw) / 2
      if (sh < this.containerHeight) this.bounds.minY = this.bounds.maxY = (this.containerHeight - sh) / 2
      this.position.x = Math.max(this.bounds.minX, Math.min(this.bounds.maxX, this.position.x))
      this.position.y = Math.max(this.bounds.minY, Math.min(this.bounds.maxY, this.position.y))
      this.updateVisibleBooths()
      this.scheduleDraw()
    },
    onImageLoad(e) {
  this.imageWidth = e.target.naturalWidth
  this.imageHeight = e.target.naturalHeight
  this.updateContainerInfo().then(() => {
    this.resetToFit()
    // 图片加载完成强制绘制展位
    setTimeout(() => this.drawBooths(), 200)
  })
},
    resetToFit() {
      if (!this.imageWidth || !this.containerWidth) return
      const sx = this.containerWidth / this.imageWidth
      const sy = this.containerHeight / this.imageHeight
      this.scale = Math.max(this.minScale, Math.min(Math.min(sx, sy), this.maxScale))
      this.lastScale = this.scale
      this.updateBounds()
      this.position.x = this.bounds.minX
      this.position.y = this.bounds.minY
      this.$nextTick(() => setTimeout(() => this.drawBooths(), 100))
    },
    reset() { this.resetToFit() },
    onImageError() { this.$message.error('平面图加载失败') },
    updateVisibleBooths() {
      this.visibleBooths.clear()
      const vl = -this.position.x / this.scale
      const vt = -this.position.y / this.scale
      const vr = vl + this.containerWidth / this.scale
      const vb = vt + this.containerHeight / this.scale
      this.viewportCache = { left: vl, top: vt, right: vr, bottom: vb }
      this.booths.forEach(b => {
        const box = this.getBoothBounds(b)
        if (!(box.right < vl || box.left > vr || box.bottom < vt || box.top > vb)) {
          this.visibleBooths.add(b.id)
        }
      })
    },
    getBoothBounds(booth) {
      if (booth.bounds) return booth.bounds
      const xs = booth.points.map(p => p.x)
      const ys = booth.points.map(p => p.y)
      booth.bounds = { left: Math.min(...xs), top: Math.min(...ys), right: Math.max(...xs), bottom: Math.max(...ys) }
      return booth.bounds
    },
    // Canvas绘制展位
    drawBooths() {
      const now = Date.now()
      if (now - this.lastDrawTime < this.drawThrottle) return
      this.lastDrawTime = now
      if (!this.imageWidth || !this.containerWidth) return
      const canvas = document.querySelector('.map-canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = this.containerWidth
      canvas.height = this.containerHeight
      ctx.clearRect(0, 0, this.containerWidth, this.containerHeight)
      this.booths.forEach(b => {
        if (!this.visibleBooths.has(b.id)) return
        const pts = b.points.map(p => ({ x: p.x * this.scale + this.position.x, y: p.y * this.scale + this.position.y }))
        ctx.beginPath()
        ctx.moveTo(pts[0].x, pts[0].y)
        pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y))
        ctx.closePath()
        if (b.selected) {
          ctx.fillStyle = '#007AFF'
          ctx.globalAlpha = 0.7
        } else if (b.searched) {
          ctx.fillStyle = '#FFD700'
          ctx.globalAlpha = 0.6
        } else {
          ctx.fillStyle = b.color
          ctx.globalAlpha = 0.5
        }
        ctx.fill()
        ctx.globalAlpha = 1
        if (b.selected) {
          ctx.strokeStyle = '#0055CC'
          ctx.lineWidth = Math.max(2, 3 * this.scale)
        } else if (b.searched) {
          ctx.strokeStyle = '#f00'
          ctx.lineWidth = Math.max(3, 4 * this.scale)
        } else {
          ctx.strokeStyle = b.occupied ? '#c00' : '#0c0'
          ctx.lineWidth = Math.max(1, 2 * this.scale)
        }
        ctx.stroke()
        if (this.scale > 0.2) {
          ctx.fillStyle = b.searched ? '#000' : '#333'
          ctx.font = `${Math.max(8, 12 * this.scale)}px sans-serif`
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length
          const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length
          if (b.searched) {
            const tw = ctx.measureText(b.name).width
            ctx.fillStyle = '#ff0'
            ctx.globalAlpha = 0.8
            ctx.fillRect(cx - tw / 2 - 2, cy - 8, tw + 4, 16)
            ctx.fillStyle = '#000'
            ctx.globalAlpha = 1
          }
        }
      })
    },
    scheduleDraw() { this.debouncedDraw() },
    // 手势缩放拖拽
    onGestureStart(e) {
      if (e.touches.length === 2) {
        this.isPinching = true
        this.pinchStartScale = this.scale
        this.pinchStartPosition = { ...this.position }
        this.pinchStartDistance = this.getTouchDistance(e.touches)
        this.pinchStartCenter = this.getTouchCenter(e.touches)
        this.showZoomHint = true
        setTimeout(() => this.showZoomHint = false, 2000)
      }
    },
    onGestureChange(e) {
      if (e.touches.length !== 2 || !this.isPinching) return
      const dist = this.getTouchDistance(e.touches)
      const factor = dist / this.pinchStartDistance
      let newScale = this.pinchStartScale * factor
      newScale = Math.max(this.minScale, Math.min(this.maxScale, newScale))
      const center = this.getTouchCenter(e.touches)
      const rx = (center.x - this.pinchStartPosition.x) / this.pinchStartScale
      const ry = (center.y - this.pinchStartPosition.y) / this.pinchStartScale
      this.scale = newScale
      this.position.x = center.x - rx * newScale
      this.position.y = center.y - ry * newScale
      this.updateBounds()
      this.scheduleDraw()
    },
    onGestureEnd() {
      this.isPinching = false
      this.enforceBounds()
    },
   onTouchStart(e) {
  e.preventDefault() // 阻止页面滚动，触摸拖拽顺滑
  this.ensureContainerReady()
  if (e.touches.length === 1) {
    this.isMoving = true
    this.startX = e.touches[0].clientX - this.position.x
    this.startY = e.touches[0].clientY - this.position.y
  } else if (e.touches.length === 2) {
    this.onGestureStart(e)
  }
  this.onCanvasTouch(e)
},
    onTouchMove(e) {
      const now = Date.now()
      if (now - this.lastTouchTime < 8) return
      this.lastTouchTime = now
      if (e.touches.length === 1 && this.isMoving) {
        const nx = e.touches[0].clientX - this.startX
        const ny = e.touches[0].clientY - this.startY
        this.position.x = Math.max(this.bounds.minX, Math.min(this.bounds.maxX, nx))
        this.position.y = Math.max(this.bounds.minY, Math.min(this.bounds.maxY, ny))
        this.updateVisibleBooths()
        this.scheduleDraw()
      } else if (e.touches.length === 2) {
        this.onGestureChange(e)
      }
    },
    onTouchEnd() {
      this.isMoving = false
      this.isScaling = false
      this.enforceBounds()
    },
    enforceBounds() {
      this.position.x = Math.max(this.bounds.minX, Math.min(this.bounds.maxX, this.position.x))
      this.position.y = Math.max(this.bounds.minY, Math.min(this.bounds.maxY, this.position.y))
      this.scheduleDraw()
    },
    getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    },
    getTouchCenter(touches) {
      return { x: (touches[0].clientX + touches[1].clientX) / 2, y: (touches[0].clientY + touches[1].clientY) / 2 }
    },
    // 点击展位判断
   async onCanvasTouch(e) {
  await this.ensureContainerReady()
  const touch = e.touches[0]
  const rect = this.mapAreaRect
  // 打印坐标，调试是否正确
  console.log('触摸坐标', touch.clientX - rect.left, touch.clientY - rect.top)
  const cx = touch.clientX - rect.left
  const cy = touch.clientY - rect.top
  if (this.batchSelectionMode) {
    this.handleBatchSelection(cx, cy)
  } else {
    this.handleSingleSelection(cx, cy)
  }
},
    handleSingleSelection(cx, cy) {
      const ix = (cx - this.position.x) / this.scale
      const iy = (cy - this.position.y) / this.scale
      let hit = false
      for (const b of this.booths) {
        if (!this.visibleBooths.has(b.id)) continue
        if (this.isPointInPolygon(ix, iy, b.points)) {
          this.showBoothDialog(b)
          hit = true
          break
        }
      }
      if (!hit) console.log('未点击展位')
    },
    // 查看展位详情接口
    async showBoothDialog(booth) {
      const param = {    access_token: sessionStorage.getItem('token'), company_id: sessionStorage.getItem('company_id'), id: booth.id }

      const res = await this.$axios.findBoothOne(param)
      if (res.code === 200) {
        const item = res.data
        const isOcc = item.status === 1
        this.currentBooth = {
          id: item.id,
          name: item.sn,
          points: [{ x: item.x1, y: item.y1 }, { x: item.x2, y: item.y2 }, { x: item.x3, y: item.y3 }, { x: item.x4, y: item.y4 }],
          selected: false,
          searched: false,
          occupied: isOcc,
          color: isOcc ? '#f00' : '#0f0',
          area: item.area,
          amount: item.amount,
          sn: item.sn,
          orderinfo: item.orderinfo
        }
        this.showBoothDetail = true
      }
    },
    closeBoothDialog() {
      this.showBoothDetail = false
      this.currentBooth = null
    },
    // 选择展位弹出支付
    selectCurrentBooth() {
      if (!this.currentBooth) return
      this.booths.forEach(b => b.selected = b.id === this.currentBooth.id)
      this.toggleBoothSelection(this.currentBooth)
      this.closeBoothDialog()
      setTimeout(() => {
        this.isPay = true
        this.paymentMethod = 'wechat'
        this.orderInfo = {
          orderNumber: 'A' + Date.now(),
          sn: this.currentBooth.sn,
          area: this.currentBooth.area,
          totalAmount: this.currentBooth.amount
        }
      }, 300)
    },
    toggleBoothSelection(booth) {
      booth.selected = !booth.selected
      this.scheduleDraw()
      this.$message(booth.selected ? `已选${booth.name}` : `取消${booth.name}`)
    },
    toggleSelectionMode() {
      this.batchSelectionMode = !this.batchSelectionMode
      this.selectionQueue = []
      this.$message(this.batchSelectionMode ? '批量选择开启' : '批量选择关闭')
    },
    clearSelection() {
      this.booths.forEach(b => b.selected = false)
      this.selectionQueue = []
      this.scheduleDraw()
      this.$message.success('已清空所有选择')
    },
    removeBooth(id) {
      const booth = this.boothMap.get(id)
      if (booth) {
        booth.selected = false
        this.scheduleDraw()
      }
    },
    // 射线法判断点在多边形
    isPointInPolygon(x, y, pts) {
      if (pts.length < 3) return false
      let inside = false
      for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        const xi = pts[i].x, yi = pts[i].y
        const xj = pts[j].x, yj = pts[j].y
        const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)
        if (intersect) inside = !inside
      }
      return inside
    },
    zoomIn() {
      const old = this.scale
      this.scale = Math.min(this.maxScale, this.scale * 1.2)
      this.updateBounds()
      const cX = this.containerWidth / 2
      const cY = this.containerHeight / 2
      const rX = (cX - this.position.x) / old
      const rY = (cY - this.position.y) / old
      this.position.x = cX - rX * this.scale
      this.position.y = cY - rY * this.scale
      this.enforceBounds()
      this.scheduleDraw()
    },
    zoomOut() {
      const old = this.scale
      this.scale = Math.max(this.minScale, this.scale / 1.2)
      this.updateBounds()
      const cX = this.containerWidth / 2
      const cY = this.containerHeight / 2
      const rX = (cX - this.position.x) / old
      const rY = (cY - this.position.y) / old
      this.position.x = cX - rX * this.scale
      this.position.y = cY - rY * this.scale
      this.enforceBounds()
      this.scheduleDraw()
    },
    // 支付相关
    checkIconResources() {
      this.hasWechatIcon = true
      this.hasOfflineIcon = true
    },
    selectPaymentMethod(method) {
      this.$nextTick(() => this.paymentMethod = method)
    },
    closePayDialog() {
      this.isPay = false
      this.paymentMethod = 'wechat'
      this.currentBooth = null
    },
    wxPay() {},
    async goPay() {
      if (this.isPaying || !this.paymentMethod) {
        this.$message.warning('请选择支付方式')
        return
      }
      const booth = this.currentBooth
      if (!booth) return
      this.isPaying = true
      this.$loading({ text: '提交订单中' })
      try {
        const params = {
          access_token: sessionStorage.getItem('token'), 
          company_id: sessionStorage.getItem('company_id'),
        // user_id: store.state.app.uid,
    
          expo_id: this.expo_id,
          booth_id: booth.id,
          booth_sn: booth.sn,
          amount: booth.amount,
          pay_type: this.paymentMethod === 'wechat' ? 1 : 2
        }
        const res = await this.$axios.$axiosboothCreateOrder(params)
        this.$loading().close()
        if (res.code === 200) {
          const order = res.data
          if (this.paymentMethod === 'wechat') {
            if (order.payInfo) this.wxPay(order.payInfo)
            else this.$message.error('获取支付参数失败')
          } else {
            this.closePayDialog()
            this.$alert(`订单号：${order.order_no}\n请联系工作人员线下缴费`, '下单成功', {
              confirmButtonText: '去我的订单',
              callback: () => this.$router.push('/subpages/user/pages/booth/center')
            })
          }
        } else {
          this.$message.error(res.msg || '下单失败')
        }
      } catch (err) {
        this.$loading().close()
        this.$message.error('网络异常，请重试')
      } finally {
        this.isPaying = false
      }
    },
    handleBatchSelection(cx, cy) {
      const ix = (cx - this.position.x) / this.scale
      const iy = (cy - this.position.y) / this.scale
      for (const b of this.booths) {
        if (!this.visibleBooths.has(b.id)) continue
        if (this.isPointInPolygon(ix, iy, b.points)) {
          this.addToSelectionQueue(b.id)
        }
      }
      this.processSelectionQueue()
    },
    addToSelectionQueue(id) {
      if (!this.selectionQueue.includes(id)) this.selectionQueue.push(id)
    },
    async processSelectionQueue() {
      if (this.isProcessingQueue || !this.selectionQueue.length) return
      this.isProcessingQueue = true
      try {
        while (this.selectionQueue.length > 0) {
          const batch = this.selectionQueue.splice(0, 50)
          batch.forEach(id => {
            const b = this.boothMap.get(id)
            if (b) b.selected = true
          })
          this.scheduleDraw()
          await new Promise(res => setTimeout(res, 0))
        }
        this.$message.success('批量选择完成')
      } catch (e) {
        console.error(e)
      } finally {
        this.isProcessingQueue = false
      }
    }
  }
}
</script>

<style scoped>
.ali-container {
  padding: 16px;
  min-height: 100vh;
  background-color: #f5f7fa;
  box-sizing: border-box;
  padding-bottom: 60px;
}

/* 清除卡片内边距，容器高度正常 */
/deep/ .el-card {
  border-radius: 12px;
  margin-bottom: 16px;
  border: none;
}
/deep/ .el-card__body {
  padding: 0 !important;
  height: 100%;
}

.map-card {
  height: calc(100vh - 420px);
  overflow: hidden;
}

/* 核心：允许触摸操作，禁止浏览器滚动劫持 */
.map-area {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  touch-action: manipulation;
  user-select: none;
  background: #ffffff;
  /* 禁止鼠标选中文本干扰拖拽 */
  cursor: grab;
}
.map-area:active {
  cursor: grabbing;
}

.map-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  pointer-events: auto;
  cursor: inherit;
}

/* 弹窗打开时canvas穿透点击到底图 */
.canvas-behind {
  z-index: 1 !important;
  pointer-events: none !important;
}

.map-content {
  position: absolute;
  top: 0;
  left: 0;
  will-change: transform;
  transition: transform 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
.map-content.scaling {
  transition: none;
}

.map-image {
  display: block;
  pointer-events: none;
}

.zoom-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 8px 16px;
  border-radius: 20px;
  z-index: 99;
  animation: fade 2s ease forwards;
}
@keyframes fade {
  0% { opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { opacity: 0; }
}

.scale-text {
  margin: 0 12px;
  color: #666;
}

.stat-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
}
.stat-label {
  width: 80px;
  color: #666;
}
.stat-value {
  flex: 1;
  font-weight: 500;
}
.stat-value.occupied {
  color: #f53f3f;
}
.stat-value.available {
  color: #00b42a;
}
.stat-percent {
  font-size: 12px;
  color: #999;
  margin-left: 6px;
}
.color-dot {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  margin-left: 8px;
}
.color-dot.red { background: #f53f3f; }
.color-dot.green { background: #00b42a; }
.color-dot.blue { background: #1677ff; }
.color-dot.gold { background: #ffd700; }

.legend-title {
  font-weight: 500;
  margin-bottom: 8px;
}
.legend-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.legend-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #666;
}

.result-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.result-btns {
  display: flex;
  gap: 8px;
}
.result-list {
  max-height: 120px;
  overflow-y: auto;
}
.result-item {
  padding: 6px 0;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}
.result-item.selected {
  color: #1677ff;
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ddd;
}
.dot.active {
  background: #1677ff;
}

.selected-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.selected-list {
  max-height: 120px;
  overflow-y: auto;
}
.selected-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #eee;
}
.close-tag {
  color: #f53f3f;
  cursor: pointer;
}

.booth-detail {
  font-size: 14px;
}
.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}
.label {
  color: #666;
}
.value {
  color: #333;
}
.value.price {
  color: #f53f3f;
  font-weight: 500;
}
.text-red {
  color: #f53f3f;
  font-weight: 500;
}
.text-green {
  color: #00b42a;
  font-weight: 500;
}
.text-blue {
  color: #1677ff;
  font-weight: 500;
}
.dialog-footer {
  text-align: right;
}

.pay-dialog-wrap {
  font-size: 14px;
}
.pay-block {
  margin-bottom: 20px;
}
.block-title {
  font-size: 15px;
  font-weight: 500;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
  margin-bottom: 12px;
}
.order-info-item {
  display: flex;
  justify-content: space-between;
  padding: 9px 0;
}
.info-label {
  color: #666;
}
.info-value {
  color: #333;
}
.order-info-item.total .info-value {
  font-size: 16px;
}
.total-price {
  color: #f53f3f;
  font-weight: bold;
}

.pay-method-list {
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
}
.pay-method-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}
.pay-method-item:last-child {
  border-bottom: none;
}
.pay-method-item.active {
  background: rgba(22, 119, 255, 0.06);
}
.method-left {
  display: flex;
  align-items: center;
}
.icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin-right: 12px;
}
.icon.wechat {
  background: #07c160;
}
.icon.offline {
  background: #ff7d00;
}
.method-text .name {
  font-size: 14px;
  color: #333;
}
.method-text .desc {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}
.radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid #dcdcdc;
  position: relative;
}
.radio.checked {
  border-color: #1677ff;
}
.radio.checked::after {
  content: "";
  width: 10px;
  height: 10px;
  background: #1677ff;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.pay-tip {
  background: #fff7e6;
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  align-items: flex-start;
  margin-top: 12px;
}
.tip-icon {
  color: #ff7d00;
  margin-right: 8px;
  font-size: 16px;
}
.tip-text {
  color: #ff7d00;
  font-size: 13px;
  line-height: 1.5;
}

.tab-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 50px;
  background: #fff;
  border-top: 1px solid #eee;
  display: flex;
  z-index: 999;
}
.tab-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
  cursor: pointer;
}
.tab-item.active {
  color: #1677ff;
}
.tab-text {
  font-size: 12px;
  margin-top: 4px;
}

@keyframes fade {
  0% { opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { opacity: 0; }
}
</style>